import { message, Drawer, Form, Radio, Input, Space, Button, Upload, Typography, Image } from 'antd';
import SiteModuleDrawerOperationLogExtra from '../SiteModuleDrawerOperationLogExtra';
import { fullWidthPageFormItemLayout } from '@utils/property';
import { useState, useEffect, useRef } from 'react';
import { uploadFileApi } from '@services/SiteModule';
import '../../SiteModules.less';
import config from '@config';

// 将图片字段规范为路径字符串，兼容历史脏数据
const normalizeImageField = value => {
    if (typeof value === 'string' && value) {
        return value;
    }
    if (Array.isArray(value) && value.length && value[0]?.url) {
        const url = value[0].url;
        if (url.includes(config.OSS_PREFIX)) {
            const re = new RegExp(config.OSS_PREFIX + '(.*)');
            return url.match(re)?.[1] || url;
        }
        return url;
    }
    if (value && typeof value === 'object' && Array.isArray(value.fileList)) {
        const uploaded = value.fileList.find(item => item.url);
        if (uploaded?.url) {
            const url = uploaded.url;
            if (url.includes(config.OSS_PREFIX)) {
                const re = new RegExp(config.OSS_PREFIX + '(.*)');
                return url.match(re)?.[1] || url;
            }
            return url;
        }
    }
    return '';
};

// 线上展区
const ExhibitionModuleDrawer = props => {
    const { drawerState, setDrawerState, currentModule, updateModuleSettings } = props;

    const [form] = Form.useForm();
    const exhibitionList = Form.useWatch('exhibitionList', { form, preserve: true }) || [];

    const uploadingCountRef = useRef(0);
    const [uploading, setUploading] = useState(false);

    const startUpload = () => {
        uploadingCountRef.current += 1;
        setUploading(true);
    };

    const endUpload = () => {
        uploadingCountRef.current = Math.max(0, uploadingCountRef.current - 1);
        if (uploadingCountRef.current === 0) {
            setUploading(false);
        }
    };

    // 获取上传文件 - 前缀
    const getFileUrl = url => {
        if (typeof url !== 'string' || !url) {
            return null;
        }
        if (url.includes('https')) {
            return url;
        }
        return config.OSS_PREFIX + url;
    };

    // 上传文件 - logo
    const upload = async (value, index) => {
        startUpload();
        try {
            const formData = new FormData();
            formData.append('file', value.file);
            formData.append('type', 14);
            const res = await uploadFileApi(formData);
            if (res && res.status_code === 200) {
                const currentValue = [...(form.getFieldValue('exhibitionList') || [])];
                currentValue[index] = { ...currentValue[index], logo: res.data };
                form.setFieldValue('exhibitionList', currentValue);
            }
        } finally {
            endUpload();
        }
    };

    // 上传图片 - 展区图
    const uploadImg = async (value, index) => {
        startUpload();
        try {
            const formData = new FormData();
            formData.append('file', value.file);
            formData.append('type', 14);
            const res = await uploadFileApi(formData);
            if (res && res.status_code === 200) {
                const currentValue = [...(form.getFieldValue('exhibitionList') || [])];
                currentValue[index] = { ...currentValue[index], img: res.data };
                form.setFieldValue('exhibitionList', currentValue);
            }
        } finally {
            endUpload();
        }
    };

    // 上传头像格式校验
    const beforeUpload = file => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
        if (!isJpgOrPng) {
            message.error('只能上传 jpg/png/jpeg 格式');
            return false;
        }
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('图片大小不超过5MB');
            return false;
        }
        return isJpgOrPng && isLt5M;
    };

    const closeDrawer = () => setDrawerState({ ...drawerState, visible: false });

    const validateExhibitionList = list => {
        if (!list || !list.length) {
            return true;
        }
        for (let i = 0; i < list.length; i++) {
            const item = list[i];
            if (!item?.name?.trim()) {
                message.error(`第 ${i + 1} 个展区请输入企业名称`);
                return false;
            }
            if (!item.logo || typeof item.logo !== 'string') {
                message.error(`第 ${i + 1} 个展区请上传 LOGO`);
                return false;
            }
            if (!item.img || typeof item.img !== 'string') {
                message.error(`第 ${i + 1} 个展区请上传展区图`);
                return false;
            }
        }
        return true;
    };

    // 新增模块
    const onFinish = async values => {
        if (uploading) {
            message.warning('图片上传中，请稍后再提交');
            return;
        }

        const list = form.getFieldValue('exhibitionList') || [];
        if (!validateExhibitionList(list)) {
            return;
        }

        const params = {
            ...values,
            id: currentModule.id,
            data: JSON.stringify({ exhibitionList: list }),
        };
        delete params.exhibitionList;
        updateModuleSettings(params);
    };

    useEffect(() => {
        if (currentModule && drawerState?.visible) {
            let exhibitionListData = [];
            if (currentModule.data) {
                const data = JSON.parse(currentModule.data);
                exhibitionListData = (data.exhibitionList || []).map(item => ({
                    ...item,
                    logo: normalizeImageField(item.logo),
                    img: normalizeImageField(item.img),
                }));
            }
            form.setFieldsValue({
                displayTitle: currentModule.displayTitle,
                displayTitleEn: currentModule.displayTitleEn,
                hiddenWeb: currentModule.hiddenWeb,
                hiddenH5: currentModule.hiddenH5,
                status: currentModule.status,
                path: currentModule.path,
                exhibitionList: exhibitionListData,
            });
        }
    }, [currentModule, drawerState?.visible, form]);

    return (
        <Drawer
            closable={{ placement: 'end' }}
            title={`模块配置：${currentModule?.displayTitle}`}
            className="applog-record-drawer"
            size="100%"
            placement="bottom"
            push={false}
            open={drawerState?.moduleType === 9 && drawerState.visible}
            onClose={closeDrawer}
            footer={
                <div className="custom-drawer-footer" style={{ textAlign: 'right' }}>
                    <Space>
                        <Button onClick={closeDrawer}>取消</Button>
                        <Button type="primary" form="ExhibitionModuleForm" htmlType="submit" loading={uploading} disabled={uploading}>
                            确定
                        </Button>
                    </Space>
                </div>
            }
        >
            <SiteModuleDrawerOperationLogExtra siteModuleId={currentModule?.id} />
            <Form name="ExhibitionModuleForm" form={form} {...fullWidthPageFormItemLayout} onFinish={onFinish}>
                <Form.Item name="displayTitle" label="显示标题" rules={[{ required: true, message: '显示标题不能为空' }]}>
                    <Input placeholder="请输入模块显示标题" />
                </Form.Item>

                <Form.Item name="displayTitleEn" label="英文显示标题">
                    <Input placeholder="请输入模块英文显示标题" />
                </Form.Item>

                <Form.Item
                    name="path"
                    label="访问路径"
                    rules={[
                        { required: true, message: '访问路径不能为空' },
                        { pattern: /^[a-z\d_]{2,20}$/, message: '访问路径不符合格式要求' },
                    ]}
                    help={
                        <>
                            填写访问该模块的访问路径，如会议简介访问路径 <b>introduction</b>
                            <br />
                            格式合求：小写字母、数字或下划线（_），限2-20个字符
                        </>
                    }
                >
                    <Input placeholder="请输入模块访问路径" minLength={2} maxLength={20} showCount disabled={currentModule?.moduleType <= 10} />
                </Form.Item>
                <Form.Item label="线上展区" name="exhibitionList">
                    <Form.List name="exhibitionList">
                        {(fields, { add, remove, move }) => (
                            <>
                                <Form.Item>
                                    <Button type="link" size="small" onClick={() => add()}>
                                        <i className="bi bi-plus-circle"></i>
                                        添加展区
                                    </Button>
                                </Form.Item>
                                {fields.map(({ key, name }, index) => (
                                    <div key={key} className="exhibition-add">
                                        <Space key={key} style={{ display: 'flex' }} align="center">
                                            <div style={{ paddingRight: '20px' }}>
                                                <Form.Item label="企业名称" name={[name, 'name']} rules={[{ required: true, message: '请输入企业名称' }]}>
                                                    <Input style={{ width: 300 }} placeholder="请输入企业名称" maxLength={50} />
                                                </Form.Item>
                                                <Form.Item label="官网链接" name={[name, 'link']}>
                                                    <Input style={{ width: 300 }} placeholder="请输入官网链接" maxLength={500} />
                                                </Form.Item>
                                                <Form.Item
                                                    required
                                                    label="LOGO"
                                                    className="organizationUploadWrap"
                                                    help={
                                                        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 10 }}>
                                                            LOGO尺寸336×120px，支持JPG/PNG格式，文件大小不超过5MB
                                                        </Typography.Text>
                                                    }
                                                >
                                                    {exhibitionList[index]?.logo ? (
                                                        <div className="imageWarp">
                                                            <Image width={102} src={getFileUrl(exhibitionList[index].logo)} alt="" />
                                                        </div>
                                                    ) : null}
                                                    <Upload customRequest={e => upload(e, index)} listType="picture-card" fileList={[]} showUploadList={{ showRemoveIcon: false }} beforeUpload={beforeUpload} accept="image/jpeg, image/png, image/jpg">
                                                        <div>
                                                            <i className="bi bi-plus"></i>
                                                            <div style={{ marginTop: 8 }}>{exhibitionList[index]?.logo ? '更改图片' : '上传图片'}</div>
                                                        </div>
                                                    </Upload>
                                                </Form.Item>
                                                <Form.Item
                                                    required
                                                    label="展区图"
                                                    className="organizationUploadWrap"
                                                    style={{ marginBottom: 0 }}
                                                    help={
                                                        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 10 }}>
                                                            展区图尺寸690x236px，支持JPG/PNG格式，文件大小不超过5MB
                                                        </Typography.Text>
                                                    }
                                                >
                                                    {exhibitionList[index]?.img ? (
                                                        <div className="imageWarp">
                                                            <Image width={102} src={getFileUrl(exhibitionList[index].img)} alt="" />
                                                        </div>
                                                    ) : null}
                                                    <Upload customRequest={e => uploadImg(e, index)} listType="picture-card" fileList={[]} showUploadList={{ showRemoveIcon: false }} beforeUpload={beforeUpload} accept="image/jpeg, image/png, image/jpg">
                                                        <div>
                                                            <i className="bi bi-plus"></i>
                                                            <div style={{ marginTop: 8 }}>{exhibitionList[index]?.img ? '更改图片' : '上传图片'}</div>
                                                        </div>
                                                    </Upload>
                                                </Form.Item>
                                            </div>

                                            <span
                                                className="anticon"
                                                onClick={() => {
                                                    remove(name);
                                                }}
                                            >
                                                <i className="bi bi-dash-circle"></i>
                                            </span>
                                            {index !== 0 ? (
                                                <span
                                                    className="anticon"
                                                    onClick={() => {
                                                        move(index, index - 1);
                                                    }}
                                                >
                                                    <i className="bi bi-arrow-up"></i>
                                                </span>
                                            ) : null}
                                            {index !== fields.length - 1 ? (
                                                <span
                                                    className="anticon"
                                                    onClick={() => {
                                                        move(index, index + 1);
                                                    }}
                                                >
                                                    <i className="bi bi-arrow-down"></i>
                                                </span>
                                            ) : null}
                                        </Space>
                                    </div>
                                ))}
                            </>
                        )}
                    </Form.List>
                </Form.Item>
                <Form.Item
                    name="status"
                    label="导航状态"
                    rules={[
                        {
                            required: true,
                        },
                    ]}
                >
                    <Radio.Group>
                        <Radio value={1}>开启</Radio>
                        <Radio value={2}>敬请期待</Radio>
                    </Radio.Group>
                </Form.Item>
                <Form.Item name="hiddenWeb" label="Web端是否隐藏" rules={[{ required: true }]}>
                    <Radio.Group>
                        <Radio value={0}>显示</Radio>
                        <Radio value={1}>隐藏</Radio>
                    </Radio.Group>
                </Form.Item>

                <Form.Item name="hiddenH5" label="H5端是否隐藏" rules={[{ required: true }]}>
                    <Radio.Group>
                        <Radio value={0}>显示</Radio>
                        <Radio value={1}>隐藏</Radio>
                    </Radio.Group>
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default ExhibitionModuleDrawer;
